import type { ReactNode } from "react";
import { UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { OpenTable } from "../../../../api/reservations";
import { fmtEuro } from "../../../../utils/currency";
import { MINUTE_MS } from "../../logic/dayTime";
import { useReservationFormat } from "../../hooks/useReservationFormat";

// Неразделими интервали: двете части („Маса 41" и „отворена преди 1 ч 9 мин")
// не се пренасят по средата — на тесен екран втората минава цяла на нов ред.
const keepTogether = (text: string) => text.replace(/ /g, "\u00A0");

/**
 * Отворена сметка в „Настани" — сервитьор, маса, от колко време е отворена и
 * сумата ѝ (32.50 € срещу 0.00 € — при кого реално седят гостите). Общ ред
 * за „При кой сервитьор да настаним?" (кликът настанява) и за „Към коя
 * сметка" при резервация с няколко маси (кликът избира — `selected`,
 * отметката е `trailing`).
 */
const SeatBillRow = ({
  bill,
  nowMs,
  selected,
  disabled,
  trailing,
  onClick,
}: {
  bill: OpenTable;
  nowMs: number;
  // Само при избор (радио): избрана ли е; при действие — без.
  selected?: boolean;
  disabled: boolean;
  // Вдясно след сумата: стрелка/спинер или отметката при избор.
  trailing?: ReactNode;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const minutes = Math.floor((nowMs - Date.parse(bill.openedAt)) / MINUTE_MS);
  const opened =
    minutes < 1
      ? t("reservations.seatPicker.openedJustNow")
      : t("reservations.seatPicker.openedAgo", { duration: format.duration(minutes) });

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={selected}
      className={`flex min-h-[56px] w-full cursor-pointer items-center gap-3 rounded-[10px] border px-3.5 py-2.5 text-left transition-colors duration-150 hover:border-brand hover:bg-brand-tint disabled:cursor-not-allowed disabled:opacity-60 ${
        selected ? "border-brand bg-brand-tint" : "border-shade-3 bg-surface"
      }`}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-[9px] bg-fill text-[15px] text-body">
        <UserOutlined />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[14.5px] font-bold leading-[1.3] text-ink">
          {bill.waiterName ?? t("reservations.seatPicker.unknownWaiter")}
        </span>
        {/* break-words — само ако една неразделима част не се събира на ред (дълго име на маса). */}
        <span className="mt-0.5 break-words text-[12.5px] font-medium tabular-nums text-body">
          {keepTogether(t("reservations.seatPicker.table", { name: bill.tableName }))}&nbsp;· {keepTogether(opened)}
        </span>
      </span>
      <span className="shrink-0 text-[14px] font-bold tabular-nums text-ink">{fmtEuro(bill.totalAmount)}</span>
      {trailing && <span className="shrink-0 text-[12px] text-muted">{trailing}</span>}
    </button>
  );
};

export default SeatBillRow;
